// FE#360 — at-most-once ledger for scheduled WhatsApp Status posts.
//
// A duplicate Status is PUBLIC and cannot be un-posted, so the failure modes are
// not symmetric: re-posting is far worse than not posting. This ledger enforces
// at-most-once per scheduled message on the engine that runs the post.
//
// It exists because the messageSecret tag (the primary guard) cannot cover
// everything:
//   * a TEXT status is posted via `sendStatusTextMsgAction({color, font, text})`,
//     which has no field to carry a tag at all;
//   * even for media, the tag is only readable once the posted message is in this
//     engine's store — a re-dispatch that arrives before it lands would miss it.
//
// The ordering is what makes it safe. The marker is written BEFORE the post, so
// the one outcome we can never observe — "WhatsApp accepted it, then the engine
// died before reporting back" — leaves the marker in place and the retry is
// refused. That is exactly the case that produced the duplicates: the post
// succeeded, the MQTT round-trip was lost, the backend re-dispatched.
//
// The marker is cleared only for failures that PROVABLY happened before WhatsApp
// saw anything (see PRE_POST_FAILURE_PATTERNS), so a genuinely-unsent Status
// stays retryable. Anything ambiguous keeps the marker.

const STORAGE_KEY = 'bt.status-post-ledger.v1';
/** Markers older than this are pruned. Comfortably longer than any retry window
 *  (the backend gives up after 5 attempts), short enough that the record cannot
 *  grow without bound. */
const MARKER_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface StatusLedgerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface StatusPostMarker {
  /** Scheduled `userMessage._id`. */
  id: string;
  /** When the post was ATTEMPTED (epoch ms) — not when it completed. */
  at: number;
}

/**
 * Failures that could only have happened before WhatsApp accepted the post:
 * nothing was sent, so the marker is cleared and a retry is allowed. Every other
 * error — a send that threw, a timeout, an unknown rejection — is ambiguous and
 * keeps the marker, because the post may well have landed.
 */
const PRE_POST_FAILURE_PATTERNS: RegExp[] = [
  // Content was rejected before anything was built.
  /nothing to post/i,
  /either text or filedataurl is required/i,
  /text, content, or filedataurl required/i,
  // The engine could not even reach a send primitive.
  /is not available in store/i,
  /chat is not available/i,
  /socket not ready/i,
  /session_not_paired/i,
  // The media never became a payload, so no send was issued.
  /invalid data url/i,
  /failed to download media url/i,
  /failed to convert downloaded media to data url/i,
  // FE#47 — a media stage (fetch / build / prep / upload) that timed out or threw
  // BEFORE sendStatusMediaMsgAction was invoked. createStatus tags these with
  // "pre-post stage"; the send stage deliberately never carries that tag.
  /createStatus pre-post stage/i,
];

/**
 * DELIBERATELY NOT in the list above: timeouts and "send threw" errors
 * (`sendStatusTextMsgAction timed out after 20000ms`, `sendStatusMediaMsgAction
 * failed: …`). They read like clean failures, but they are raised AFTER the
 * payload was handed to WhatsApp, and WhatsApp accepting a post while the
 * confirmation is lost is the precise sequence that produced the duplicate
 * Statuses. Classifying them as pre-post would release the marker and re-post a
 * Status that is already public — the bug this file exists to prevent. They stay
 * ambiguous, and the user's retry button (allowRepost) is the escape hatch.
 */

export function isPrePostFailure(error: string | undefined | null): boolean {
  const e = String(error ?? '');
  if (!e) return false;
  return PRE_POST_FAILURE_PATTERNS.some((re) => re.test(e));
}

function readAll(storage: StatusLedgerStorage): StatusPostMarker[] {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is StatusPostMarker =>
        !!m && typeof m.id === 'string' && typeof m.at === 'number',
    );
  } catch {
    // A corrupt ledger must not block sends — treat it as empty and let the
    // write path overwrite it with a well-formed value.
    return [];
  }
}

function writeAll(storage: StatusLedgerStorage, markers: StatusPostMarker[]): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(markers));
  } catch {
    // Storage full / unavailable — the tag guard still applies. Never throw
    // from the ledger: it must not be able to fail a send on its own.
  }
}

function prune(markers: StatusPostMarker[], now: number): StatusPostMarker[] {
  return markers.filter((m) => now - m.at < MARKER_TTL_MS);
}

/** True when this scheduled Status was already attempted on this engine. */
export function hasStatusPostMarker(
  storage: StatusLedgerStorage,
  id: string,
  now: number = Date.now(),
): boolean {
  if (!id) return false;
  return prune(readAll(storage), now).some((m) => m.id === id);
}

/** Record an attempt. Called BEFORE the post — see the ordering note above. */
export function markStatusPostAttempted(
  storage: StatusLedgerStorage,
  id: string,
  now: number = Date.now(),
): void {
  if (!id) return;
  const markers = prune(readAll(storage), now).filter((m) => m.id !== id);
  markers.push({ id, at: now });
  writeAll(storage, markers);
}

/** Release an attempt: ONLY for a failure that provably predates the post. */
export function clearStatusPostMarker(
  storage: StatusLedgerStorage,
  id: string,
  now: number = Date.now(),
): void {
  if (!id) return;
  const markers = prune(readAll(storage), now).filter((m) => m.id !== id);
  writeAll(storage, markers);
}

/** The browser-local ledger store, or null where there is no storage (a
 *  non-browser engine wires its own). Never throws — a storage access can
 *  itself throw in a locked-down context. */
export function defaultStatusLedgerStorage(): StatusLedgerStorage | null {
  try {
    const ls = (globalThis as { localStorage?: StatusLedgerStorage }).localStorage;
    if (!ls || typeof ls.getItem !== 'function' || typeof ls.setItem !== 'function') {
      return null;
    }
    return ls;
  } catch {
    return null;
  }
}
